/**
 * Otobüs refresh-resume wiring — see booking-flow/BookingFlowResumer.tsx and the plan.
 *
 * NOTE: bus renders its step UI by `stepIndex` and the UI shown at index N is the one
 * whose "continue" button runs config step N (`BusFlowPage.tsx`). The cart id first
 * appears after `allocate` (index 2) runs, so the first safe resume step is `passengers`
 * (index 3). No `rerunSearch` — the search step already seeds its form from
 * `flow.state.searchForm`, so a pre-Allocate refresh just lands back on step 0 prefilled.
 */
import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { BusShoppingCart } from '../../api/types/bus';
import { bookingDraftKey } from '../../booking-flow/draftStorage';
import {
  cartIsPaid,
  clampStepIndex,
  compact,
  type BookingFlowPersistConfig,
  type ResumeAdapter,
} from '../../booking-flow/resume';
import type { BusFlowState } from './flowConfig';

/** Must match `busFlowConfig.steps` order (asserted in resume.test.ts). Bump
 *  `busPersistConfig.version` on any change. Uçuş akışıyla aynı sıra: Payment,
 *  Finalize'dan ÖNCE (FinalizeShopping başarılı ödemeden sonra `runPayment` içinde). */
const BUS_STEP_ORDER = [
  'search',
  'schema',
  'allocate',
  'passengers',
  'payment',
  'finalize',
  'document',
] as const;
const idx = (id: string) => BUS_STEP_ORDER.indexOf(id as (typeof BUS_STEP_ORDER)[number]);

const FLOOR = idx('passengers');
const PAYMENT = idx('payment');
const DOCUMENT = idx('document');

const busPartialize = (s: BusFlowState): Partial<BusFlowState> =>
  compact({
    // only once the user has actually picked a route — keeps a pristine mount from
    // writing a draft
    searchForm: s.searchForm?.origin || s.searchForm?.destination ? s.searchForm : undefined,
    selectedTrip: s.selectedTrip,
    selectedSeats: s.selectedSeats?.length ? s.selectedSeats : undefined,
    passengers: s.passengers?.length ? s.passengers : undefined,
    contact: s.contact,
    selectedCurrentAccount: s.selectedCurrentAccount,
    billingInformation: s.billingInformation,
    paymentType: s.paymentType,
    paymentAmount: s.paymentAmount,
    payment: s.payment,
    shoppingCartId: s.shoppingCartId,
  });

export const busPersistConfig: BookingFlowPersistConfig<BusFlowState> = {
  key: bookingDraftKey('bus'),
  version: 4,
  floorStepId: 'passengers',
  searchResultStepId: 'schema',
  partialize: busPartialize,
  /** Onaya gönderilmiş taslağı YAZMA (uçuş/otel ile aynı). */
  shouldPersist: (s, stepIndex) => {
    if (s.sentForApproval) return false;
    if (stepIndex > 0) return true;
    return Object.keys(busPartialize(s)).length > 0;
  },
};

export const busResumeAdapter: ResumeAdapter<BusFlowState> = {
  getCartId: (draft) => draft.shoppingCartId,
  applyResumedCart: (cart: ShoppingCart) => ({
    shoppingCart: cart as unknown as BusShoppingCart,
    shoppingCartId: cart.id,
  }),
  resolveResumeStepId: (cart: ShoppingCart, savedStepId: string) => {
    // Ödenmiş ya da biletlenmiş sepet → belge adımı (yeni sırada finalize ödemeden sonra,
    // yani finalize edilmişse ödeme de yapılmış demektir).
    if (cartIsPaid(cart) || cart.isFinalized) return BUS_STEP_ORDER[DOCUMENT];
    if (cart.isPriceChanged) return BUS_STEP_ORDER[FLOOR];
    const saved = idx(savedStepId);
    // Bir sepet varsa Allocate asla tekrar oynatılmaz; ödenmemiş sepet en fazla ödeme adımına.
    return BUS_STEP_ORDER[clampStepIndex(saved >= 0 ? saved : FLOOR, FLOOR, PAYMENT)];
  },
};
